import React from 'react';
import { NavLink } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Shell shared by every /admin/* page. Renders an h1, optional description,
 * a tab strip (Organizations / Users / Rooms / …), and slot for content.
 *
 * Tabs are always rendered; access control happens at the route level.
 */
export default function AdminPage({ title, description, children }) {
  const { t } = useI18n();
  const { user } = useAuth();
  // NOTE: 'Test tools' tab (/admin/qa) is intentionally hidden from this nav
  // for now. The route + page are still wired and reachable via direct URL,
  // so we can re-enable by adding the entry back when QA injectors land.
  const tabs = [
    { to: '/admin/organizations', label: t('admin_tab_orgs') },
    { to: '/admin/users', label: t('admin_tab_users') },
    { to: '/admin/patients', label: t('admin_tab_patients') },
    { to: '/admin/rooms', label: t('admin_tab_rooms') },
    { to: '/admin/buildings', label: t('admin_tab_buildings') },
    { to: '/admin/devices', label: t('admin_tab_devices') },
    { to: '/admin/device-types', label: t('admin_tab_device_types') },
    { to: '/admin/alerts', label: t('admin_tab_alerts') },
    { to: '/admin/gateways', label: t('admin_tab_gateways') },
  ];

  return (
    <div className="space-y-8 animate-fade-up">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
          {t('nav_admin')}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-none">{title}</h1>
        {description && <p className="text-ink-soft mt-3 max-w-xl">{description}</p>}
      </header>

      <div className="border-b border-stone_line">
        <nav className="flex gap-1 -mb-px overflow-x-auto" data-testid="admin-tabs">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`admin-tab-${t.to.split('/').pop()}`}
              className={({ isActive }) =>
                `px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-sage text-sage-700'
                    : 'border-transparent text-ink-soft hover:text-ink hover:border-stone_line'
                }`
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <section>{children}</section>
    </div>
  );
}
